import React, { useState, useMemo, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import {
    Dropdown,
    DropdownMenu,
    DropdownToggle,
    Button,
} from "reactstrap";
import { useLocation, useHistory } from 'react-router-dom';
import queryParser from '../../../helpers/queryParser';
import qs from 'querystring';
import Checkbox from '../../../components/Common/checkbox';
import getArrayFromQuery from '../../../helpers/getArrayQuery';
import SearchBar from '../../../components/Common/searchBar';
import useDebounce from '../../../hooks/useDebounce';
import { MAX_SEARCH_LENGTH } from '../../../constants/leghtValidity';
import { clearPaginatedListCourse, getPaginatedCourseList } from '../../../store/actions';
import { getPaginatedPremiumArticlesList, clearPaginatedPremiumArticlesList } from '../../../store/dashboard/PremiumArticles/actions';
import { DEFAULT_COURSES_LIMIT, DEFAULT_OFFSET } from '../../../constants/pagination';
import InfiniteScroll from '../../../components/Common/InfinitScroll';


const FILTER_QUERY = "courseIds";
const PREMIUM_ARTICLES_FILTER_QUERY = "premiumArticleIds";
const SHOW_VIDEOS_ATTACHED_TO_PREMIUM_ARTICLES_QUERY = "showVideosAttachedToPremiumArticles";

function VideoLibraryFilters({ 
    toolkitProps, 
    coursesState, 
    premiumArticlesState,
    getPaginatedCourseList, 
    clearPaginatedListCourse,
    getPaginatedPremiumArticlesList,
    clearPaginatedPremiumArticlesList
}) {
    const history = useHistory();
    const location = useLocation();
    const scrollRef = useRef(null);
    

    const {
        loading,
        data: items,
        pagination,
    } = coursesState;

    const {
        loading: premiumArticlesLoading,
        data: premiumArticlesItems,
        pagination: premiumArticlesPagination,
    } = premiumArticlesState;

    const query = useMemo(() => queryParser(location.search), [location]);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterOption, setFilterOption] = useState(query[FILTER_QUERY] ?
        getArrayFromQuery(query[FILTER_QUERY]).map(id => Number(id)) : []);
    
    const [premiumArticlesFilterOption, setPremiumArticlesFilterOption] = useState(query[PREMIUM_ARTICLES_FILTER_QUERY] ?
        getArrayFromQuery(query[PREMIUM_ARTICLES_FILTER_QUERY]).map(id => Number(id)) : []);
    
    const [showVideosAttachedToPremiumArticles, setShowVideosAttachedToPremiumArticles] = useState(
        query[SHOW_VIDEOS_ATTACHED_TO_PREMIUM_ARTICLES_QUERY] === 'true'
    );

    // console.log('filterOption', filterOption);

    const [searchQuery, setSearchQuery] = useState(query.q);

    const debouncedSearchTerm = useDebounce(searchQuery);

    const disableState = useMemo(
    () => (loading || premiumArticlesLoading) || (!filterOption.length && !premiumArticlesFilterOption.length && !showVideosAttachedToPremiumArticles),
    [loading, premiumArticlesLoading, filterOption, premiumArticlesFilterOption, showVideosAttachedToPremiumArticles]
    );

    const disableLoadState = useMemo(
    () =>
        loading ||
        pagination.nextOffset > pagination.totalCount,
    [loading, pagination]
    );

    useEffect(() => {
        if (isFilterOpen) {
        clearPaginatedListCourse();
        clearPaginatedPremiumArticlesList();
        getPaginatedCourseList({
            limit: DEFAULT_COURSES_LIMIT,
            offset: DEFAULT_OFFSET,
        });
        getPaginatedPremiumArticlesList({
            limit: DEFAULT_COURSES_LIMIT,
            offset: DEFAULT_OFFSET,
        });
        }
    }, [isFilterOpen]);

    useEffect(() => {
        return () => {
            clearPaginatedListCourse();
            clearPaginatedPremiumArticlesList();
        }
    }, []);

    function handleChangeFilters(filter) {
        setFilterOption(prevState => {
            if (prevState.includes(filter)) {
                return prevState.filter(option => option !== filter);
            }
            return [...prevState, filter];
        })
    }

    function handleChangePremiumArticlesFilters(filter) {
        setPremiumArticlesFilterOption(prevState => {
            if (prevState.includes(filter)) {
                return prevState.filter(option => option !== filter);
            }
            return [...prevState, filter];
        })
    }

    function handleSubmitFilters() {
        setIsFilterOpen(prevState => !prevState);
        const searchParams = {
            ...query,
        };
        
        if (filterOption.length > 0) {
            searchParams[FILTER_QUERY] = [...filterOption];
        }
        
        if (premiumArticlesFilterOption.length > 0) {
            searchParams[PREMIUM_ARTICLES_FILTER_QUERY] = [...premiumArticlesFilterOption];
        }
        
        if (showVideosAttachedToPremiumArticles) {
            searchParams[SHOW_VIDEOS_ATTACHED_TO_PREMIUM_ARTICLES_QUERY] = 'true';
        }
        
        history.push({
            search: qs.stringify(searchParams)
        })
    }

    function clearSearch() {
        setFilterOption([]);
        setPremiumArticlesFilterOption([]);
        setShowVideosAttachedToPremiumArticles(false);
        history.replace({
            search: '',
        })
    }

    function handleInputSearch(event) {
        const search = event.target.value;

        if (search.length > MAX_SEARCH_LENGTH) {
            return;
        }
        setSearchQuery(search);
    }

    useEffect(() => {
        toolkitProps.searchProps.onSearch(debouncedSearchTerm);
    }, [debouncedSearchTerm, toolkitProps]);

    return (
        <section className="d-flex flex-row justify-content-between">
            <div className="search-box ms-2 mb-2 d-inline-block">
                <SearchBar
                    handleEnterSearch={handleInputSearch}
                    searchQuery={searchQuery}
                    inputClassName="user-list__search"
                />
            </div>
            <section className="d-flex flex-row">
                {(!!query.courseIds || !!query.premiumArticleIds || !!query.showVideosAttachedToPremiumArticles) &&
                    <Button color="primary" className="user-list__clear-button" onClick={clearSearch}>
                        Reset filters
                    </Button>
                }
                <Dropdown
                    isOpen={isFilterOpen}
                    toggle={() => setIsFilterOpen(prevState => !prevState)}
                >
                    <DropdownToggle tag="button" className="btn user-list--dropdown__toggle">
                        Filter <i className="mdi mdi-chevron-down" />
                    </DropdownToggle>
                    <DropdownMenu className="dropdown-menu-right user-list--dropdown">
                        <div className="ms-2 mb-3">
                            <Checkbox
                                value="showVideosAttachedToPremiumArticles"
                                label="Show Videos Attached to Premium Articles"
                                key="show-videos-attached-to-premium-articles"
                                checked={showVideosAttachedToPremiumArticles}
                                onChange={() => setShowVideosAttachedToPremiumArticles(prev => !prev)}
                            />
                        </div>
                        
                        <span className="user-list--dropdown__title ms-2 mb-2">Courses</span>

                         <div style={{
                                height: '200px',
                            }}>
                            {!!items.length && (
                            <InfiniteScroll
                                loadMore={() =>
                                getPaginatedCourseList({
                                    limit: DEFAULT_COURSES_LIMIT,
                                    offset: pagination.nextOffset,
                                })
                                }
                                limit={DEFAULT_COURSES_LIMIT}
                                totalCount={pagination.totalCount}
                                nextOffset={pagination.nextOffset}
                                ref={scrollRef}
                                disabled={disableLoadState}
                            >
                                {items.map((course) => (
                                <Checkbox
                                    value={course.id}
                                    label={course.title}
                                    key={`course-${course.id}`}
                                    // onChange={() => handleChangeSelectedCourses(course.id)}
                                    onChange={() => { handleChangeFilters(course.id) }}
                                    checked={filterOption.includes(course.id)}
                                />
                                ))}
                            </InfiniteScroll>
                            )}
                        </div>
                        
                        <span className="user-list--dropdown__title ms-2 mb-2 mt-3">Premium Articles</span>

                         <div style={{
                                height: '200px',
                            }}>
                            {!!premiumArticlesItems.length && (
                            <InfiniteScroll
                                loadMore={() =>
                                getPaginatedPremiumArticlesList({
                                    limit: DEFAULT_COURSES_LIMIT,
                                    offset: premiumArticlesPagination.nextOffset,
                                })
                                }
                                limit={DEFAULT_COURSES_LIMIT}
                                totalCount={premiumArticlesPagination.totalCount}
                                nextOffset={premiumArticlesPagination.nextOffset}
                                disabled={premiumArticlesLoading || premiumArticlesPagination.nextOffset > premiumArticlesPagination.totalCount}
                            >
                                {premiumArticlesItems.map((article) => (
                                <Checkbox
                                    value={article.id}
                                    label={article.title}
                                    key={`premium-article-${article.id}`}
                                    onChange={() => { handleChangePremiumArticlesFilters(article.id) }}
                                    checked={premiumArticlesFilterOption.includes(article.id)}
                                />
                                ))}
                            </InfiniteScroll>
                            )}
                        </div>
                        <Button
                            color="success"
                            className="w-100 mt-4"
                            onClick={handleSubmitFilters}
                            disabled={disableState}
                        >
                            Apply
                    </Button>
                    </DropdownMenu>
                </Dropdown>
            </section>
        </section>
    );
}

const mapStateToProps = ({ Courses: coursesState, PremiumArticles: premiumArticlesState }) => ({
  coursesState,
  premiumArticlesState,
});

export default connect(mapStateToProps, {
  getPaginatedCourseList,
  clearPaginatedListCourse,
  getPaginatedPremiumArticlesList,
  clearPaginatedPremiumArticlesList,
})(VideoLibraryFilters);